import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { useAuth } from "../../contexts/AuthContext";
import {
  rekeyCalendarMapping,
  suppressCalendarSync,
} from "../../lib/googleCalendarSync";
import { Task, SubTask, SubtaskAttachment, SubtaskLink } from "../../types";

/**
 * Returns a `convertTaskToSubtask(taskId, targetTaskId)` that demotes a
 * standalone task into a sub-task of another task. It copies the task's fields
 * onto a new sub-task under the target, re-creates its tags, attachments, and
 * links on the subtask tables, re-keys the Google Calendar mapping so the
 * existing event is preserved, then deletes the original task. Stored
 * attachment files are left in place — only the DB rows are re-pointed, the
 * same way `useConvertSubtaskToTask` leaves files under the original folder.
 *
 * Two conversions are refused up front, matching the god-hook copy: a task that
 * has its own sub-tasks (sub-tasks can't nest) and a recurring task (sub-tasks
 * have no recurrence). Both surface a toast and abort before any write.
 *
 * **Server-first with explicit rollback**, mirroring `useConvertSubtaskToTask`
 * and the pre-migration demote flow. A single database
 * transaction is not viable here: the calendar re-key is a network call to the
 * `google-calendar-sync` edge function that must run between the child-copy and
 * the original-row delete (the mapping row FK-cascades off the task), so it
 * cannot live inside a Postgres transaction. The multi-step write is therefore
 * sequential, and any step after the subtask-row insert rolls back by deleting
 * the new sub-task (cascading its partially-copied children), which leaves the
 * user exactly where they started with the original task intact.
 *
 * No priority clamp is applied in this direction. A task and a step both
 * store 1–4, and 4 is no priority. `task.priority ?? 4` stays in that range.
 *
 * Behavioural additions over the god-hook copy, matching every other migrated
 * mutation hook: failures are captured to Sentry (the god-hook used
 * `console.error`), the freshest `tasks` are read from the query cache at
 * mutation time rather than a render-time snapshot, and the cache is
 * invalidated after a successful conversion. The observable end state is
 * identical.
 *
 * This is the query-cache-aware replacement for the pre-migration demote flow.
 */
export function useConvertTaskToSubtask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return async (taskId: string, targetTaskId: string): Promise<boolean> => {
    if (!user) return false;

    // Read the freshest tasks at mutation time (not a stale render-time
    // snapshot) so the demotion sees the same cache the UI renders from.
    const currentTasks =
      queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];

    const task = currentTasks.find((t) => t.id === taskId);
    const targetTask = currentTasks.find((t) => t.id === targetTaskId);
    if (!task || !targetTask) return false;

    if ((task.subtasks?.length ?? 0) > 0) {
      toast.error(
        "Remove this task's sub-tasks before converting it to a sub-task.",
      );
      return false;
    }
    if (task.recurrence_frequency) {
      toast.error(
        "Turn off the repeat schedule before converting this task to a sub-task.",
      );
      return false;
    }

    const existingSubtasks = targetTask.subtasks || [];
    const maxSubOrder =
      existingSubtasks.length > 0
        ? Math.max(...existingSubtasks.map((st) => st.order_position))
        : 0;

    // Local rollback used whenever a step after the sub-task insert fails:
    // delete the new sub-task (which cascades any partially-copied children) so
    // the user is left exactly where they started, with the original task and
    // all its data intact.
    const rollback = async (
      createdSubtaskId: string,
      reason: string,
      cause?: unknown,
    ) => {
      Sentry.captureException(cause ?? new Error(reason), {
        extra: { mutation: "useConvertTaskToSubtask", taskId, reason },
      });
      await supabase.from("subtasks").delete().eq("id", createdSubtaskId);
      toast.error("Failed to convert to sub-task. Please try again.");
    };

    // 1. Insert the new sub-task row under the target.
    const newSubtaskId = crypto.randomUUID();
    await suppressCalendarSync("subtask", newSubtaskId, user.id);
    const { data: subtaskData, error: subtaskError } = await supabase
      .from("subtasks")
      .insert({
        id: newSubtaskId,
        task_id: targetTaskId,
        title: task.title,
        completed: task.completed,
        order_position: maxSubOrder + 100,
        description: task.description ?? "",
        priority: task.priority ?? 4,
        scheduled_date: task.scheduled_date,
        scheduled_time: task.scheduled_time,
        deadline_date: task.deadline_date,
        duration: task.duration,
        persistent: task.persistent ?? false,
      })
      .select()
      .single();

    if (subtaskError) {
      Sentry.captureException(subtaskError, {
        extra: { mutation: "useConvertTaskToSubtask", taskId },
      });
      toast.error("Failed to convert to sub-task. Please try again.");
      return false;
    }

    // 2. Copy children onto the sub-task tables. Each step aborts and rolls
    //    back if it fails, so the original task and its children are never
    //    destroyed mid-flight.
    const taskTags = task.tags || [];
    if (taskTags.length > 0) {
      const { error } = await supabase
        .from("subtask_tags")
        .insert(
          taskTags.map((tag) => ({ subtask_id: subtaskData.id, tag_id: tag.id })),
        );
      if (error) {
        await rollback(subtaskData.id, "Error migrating tags to subtask", error);
        return false;
      }
    }

    const newAttachments: SubtaskAttachment[] = [];
    const taskAttachments = task.attachments || [];
    if (taskAttachments.length > 0) {
      const { data, error } = await supabase
        .from("subtask_attachments")
        .insert(
          taskAttachments.map((a) => ({
            subtask_id: subtaskData.id,
            file_name: a.file_name,
            file_path: a.file_path,
            file_size: a.file_size,
            mime_type: a.mime_type,
            storage_url: a.storage_url,
          })),
        )
        .select();
      if (error || !data) {
        await rollback(
          subtaskData.id,
          "Error migrating attachments to subtask",
          error ?? new Error("no data returned"),
        );
        return false;
      }
      newAttachments.push(...data);
    }

    const newLinks: SubtaskLink[] = [];
    const taskLinks = task.links || [];
    if (taskLinks.length > 0) {
      const { data, error } = await supabase
        .from("subtask_links")
        .insert(
          taskLinks.map((l) => ({
            subtask_id: subtaskData.id,
            url: l.url,
            description: l.description,
          })),
        )
        .select();
      if (error || !data) {
        await rollback(
          subtaskData.id,
          "Error migrating links to subtask",
          error ?? new Error("no data returned"),
        );
        return false;
      }
      newLinks.push(...data);
    }

    const newSubtask: SubTask = {
      ...subtaskData,
      tags: taskTags,
      attachments: newAttachments,
      links: newLinks,
    };

    // 3. Move the Google Calendar mapping from the task to the new sub-task.
    //    The actual calendar event is preserved (same event id, RSVPs, etc.);
    //    only the mapping row moves between tables. If the user wasn't synced,
    //    rekey is a no-op and returns ok=true.
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const rekey = await rekeyCalendarMapping(
      { entity: "task", id: taskId },
      {
        entity: "subtask",
        id: newSubtask.id,
        parentTaskId: targetTaskId,
        fields: newSubtask,
      },
      tz,
    );
    if (!rekey.ok) {
      await rollback(subtaskData.id, "rekey-mapping failed");
      return false;
    }

    // 4. Delete the original task. Its calendar mapping was already moved in
    //    step 3, so the cascade-delete only removes the task's children (which
    //    we just copied above).
    const { error: deleteError } = await supabase
      .from("tasks")
      .delete()
      .eq("id", taskId);
    if (deleteError) {
      // The new sub-task is fully functional; surface a softer error so the
      // user knows to refresh rather than treating the conversion as a total
      // failure (which it isn't — their data is on the new sub-task).
      Sentry.captureException(deleteError, {
        extra: {
          mutation: "useConvertTaskToSubtask",
          taskId,
          reason: "delete-original-task",
        },
      });
      toast.error(
        "Converted, but the original task could not be removed. Refresh to clean up.",
      );
    }

    queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
      (prev ?? [])
        .filter((t) => t.id !== taskId)
        .map((t) =>
          t.id === targetTaskId
            ? { ...t, subtasks: [...(t.subtasks || []), newSubtask] }
            : t,
        ),
    );

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return true;
  };
}
